/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, Mutation } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Warning,
  Heading,
  Grid,
  SvgIcon,
  Select,
  InvestmentProductCard,
  InvestmentEditModal,
  InvestmentAddModal
} from '../'

import { MUTATION_CLIENT_CLIENTINVESTMENTS } from '../../mutations'


class ClientInvestment extends React.PureComponent {
  static propTypes = {
    client: PropTypes.string.isRequired,
    title: PropTypes.string,
    detailLink: PropTypes.string,
    onDetailLinkClick: PropTypes.func,
    className: PropTypes.string
  }

  state = {
    investmentStatusFilter: 'Current Employer',
    showEditModal: false,
    showAddInvestmentModal: false
  }

  toggleAddInvestmentModal = () => (
    this.setState(prevState => ({
      showAddInvestmentModal: !prevState.showAddInvestmentModal
    }))
  )

  addInvestment = (clientMutate, productName) => {
    const input = {
      id: this.props.data.client.id,
      methodCall: 'add_new_investment',
      methodParams: JSON.stringify({ "product_name": productName })
    }

    clientMutate({ variables: { input } })
      .then(() => {
        this.setState(
          { showAddInvestmentModal: false },
          this.props.data.refetch
        )
      })
      .catch(error => this.setState({ showAddInvestmentModal: false }))
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return (
        <Warning className='slds-p-around_x-large u-font-size-larger' iconSize='medium'>
          Failed to load client investment data.
        </Warning>
      )
    }

    let client = this.props.data.client

    // Filter on clientinvestment status - the mapping translates the backend statuses
    // active and deprecated into something more understandable for the frontend user
    let investments = client.clientinvestments.edges.filter(investment => {
      const investmentStatusFilterMap = {
        'Current Employer': 'ACTIVE',
        'Previous Employment': 'DEPRECATED',
      }
      return investment.node.status === investmentStatusFilterMap[this.state.investmentStatusFilter]
    })

    if (this.state.showEditModal) {
      return (
        <InvestmentEditModal
          title='Investments'
          client={client.id}
          clientInstruments={investments.map(investment => investment.node)}
          employerInstruments={investments.map(investment => investment.node.employerinstrument)}
          onCloseClick={() => this.setState({ showEditModal: false })}
        />
      )
    }

    return (
      <div className={this.props.className ? this.props.className : ''}>
        {this.props.title &&
          <Heading marginBottom={0}>
            {this.props.title}
          </Heading>
        }
        <div className='slds-m-around_medium'>
          {
            <React.Fragment>
              <div className='slds-m-top_small slds-m-bottom_small slds-grid'>
                <span className='slds-m-top_xx-small'>
                  Show:
                </span>
                <Select
                  value={this.state.investmentStatusFilter}
                  options={['Current Employer', 'Previous Employment']}
                  onChange={e => this.setState({ investmentStatusFilter: e.target.value })}
                  minWidth={150}
                  className='slds-m-left_x-small'
                />
              </div>
              {investments.length === 0  // no instruments assigned
                ? <div className='slds-m-top_large'>
                    No investments to display
                    <div
                      onClick={this.toggleAddInvestmentModal}
                      className='slds-align_absolute-center slds-size_1-of-2 slds-m-top_large u-font-size-large investment-product-add clickable clickable-link'>
                      <SvgIcon
                        variant='utility'
                        symbol='new'
                        size='large'
                        colour='light'
                        className='slds-p-right_small'
                      />
                      Add an investment
                    </div>
                  </div>
                : <Grid
                    items={[
                      ...investments.map(({ node: investment }) => (
                        <InvestmentProductCard
                          investmentId={investment.id}
                          productName={investment.employerproduct.product.name}
                          instrumentType={investment.instrument.instrumentType}
                          instrumentInternalName={investment.instrument.internalName}
                          clientContribution={investment.currentClientContribution}
                          investedBalance={investment.investedBalance}
                          processingBalance={investment.processingBalance}
                          employerContribution={investment.currentEmployerContribution}
                          detailLink={`/os/identity/${client.identity.id}/products/investments/${investment.id}`}
                          onDetailLinkClick={investmentId => this.props.onDetailLinkClick(investmentId)}
                          backgroundColour={JSON.parse(investment.employerproduct.product.info).colour_card_background}
                          footerBackgroundColour={JSON.parse(investment.employerproduct.product.info).colour_card_background_footer}
                          inverseColours={false}
                          inverseFooterColours={true} // for now we always inverse, in future we might want to set this in product.info in the backend
                        />
                      )),
                      <div
                        onClick={this.toggleAddInvestmentModal}
                        className='slds-align_absolute-center u-font-size-large investment-product-add clickable clickable-link'>
                        <SvgIcon
                          variant='utility'
                          symbol='new'
                          size='large'
                          colour='light'
                          className='slds-p-right_small'
                        />
                        Add an investment
                      </div>
                    ]}
                    itemSpacing='x-large'
                    itemClassName='slds-m-top_medium'
                  />
              }
            </React.Fragment>
          }
        </div>
        {this.state.showAddInvestmentModal &&
          <Mutation mutation={MUTATION_CLIENT_CLIENTINVESTMENTS}>
            {clientMutate => (
              <InvestmentAddModal
                title='New investment'
                clientInvestmentProducts={investments.map(({ node: investment }) => (
                  investment.employerproduct.product.name
                ))}
                activeInvestmentEmployerProducts={client.division.employer.activeEmployerproducts
                  ? JSON.parse(client.division.employer.activeEmployerproducts)
                    .filter(employerProduct => Object.keys(employerProduct)[0] === 'investments')
                    .map(employerProduct => Object.values(employerProduct)[0])
                  : []
                }
                onAddConfirmClick={productName => this.addInvestment(clientMutate, productName)}
                onCloseClick={() => this.setState({ showAddInvestmentModal: false })}
              />
            )}
          </Mutation>
        }
      </div>
    )
  }
}

const getClientInvestment = gql`
  query($id: ID!) {
    client(id: $id) {
      id
      division {
        id
        employer {
          id
          activeEmployerproducts
          instruments {
            edges {
              node {
                id
              }
            }
          }
        }
      }
      saving {
        id
        clientContribution
        employerContribution
      }
      clientinvestments {
        edges {
          node {
            id
            status
            currentClientContribution
            currentEmployerContribution
            investedBalance
            processingBalance
            employerproduct {
              id
              product {
                id
                family
                name
                info
              }
            }
            instrument {
              id
              externalName
              instrumentType
              internalName
            }
          }
        }
      }
      identity {
        id
      }
    }
  }
`

const withData =
  graphql(getClientInvestment, {
    options: ({ client }) => ({
      variables: {
        id: client
      }
    })
  })

export default withData(ClientInvestment)



// WEBPACK FOOTER //
// ./src/components/client-investment/index.js